import isBefore from "date-fns/isBefore";
import mixpanel from "mixpanel-browser";
import React, {
  useCallback,
  useState,
  MouseEvent,
  useEffect,
  Suspense,
  PropsWithChildren,
  createContext,
  useContext,
} from "react";

import LockoutChecks from "../../LockoutChecks";
import useAnalytics from "../../analytics";
import { AlertProvider } from "../../hooks/useAlert";
import useSelf from "../../hooks/useSelf";
import logOut from "../auth/logOut";
import { SelectProvider } from "../design-system/RadioGroup/useSelect";
import { ToastProvider } from "../design-system/Toaster/context";
import "./AppShell.scss";
import { BillOverdue, ContractExpiryDue } from "./BillingWarnings";
import EnforceTwoFactorAuthModal from "./EnforceTwoFactorAuthModal";
import LoadingPage from "./LoadingPage";
import Navigation from "./Navigation";
import SkipToContent from "./SkipToContent";

function AppShell({ children, showNewVersionToast }: PropsWithChildren<{ showNewVersionToast: boolean }>) {
  const self = useSelf();
  useAnalytics();

  const [isNavigationCollapsed, setIsNavigationCollapsed] = useState(
    window.localStorage.getItem("isNavigationCollapsed") === "true",
  );

  const handleOnToggleNavCollapse = useCallback(() => {
    mixpanel.track("Toggled navigation collapse", { collapsed: !isNavigationCollapsed });
    setIsNavigationCollapsed(!isNavigationCollapsed);
    window.localStorage.setItem("isNavigationCollapsed", !isNavigationCollapsed ? "true" : "false");
  }, [isNavigationCollapsed]);

  useEffect(() => {
    mixpanel.track("Loaded application", {
      collapsed: window.localStorage.getItem("isNavigationCollapsed") ?? "unset",
    });
  }, []);

  const handleOnClickLogOut = useCallback(async (event: MouseEvent) => {
    event.preventDefault();
    mixpanel.track("Log out");
    await logOut();
  }, []);

  return (
    <AlertProvider>
      <SelectProvider>
        <div className={`app-shell ${isNavigationCollapsed ? "is-nav-collapsed" : ""}`}>
          <SkipToContent />
          <ToastProvider
            showNewVersionToast={showNewVersionToast}
            showExtraMarketingPrompt={
              self.unreadFeatureAnnouncements.includes("knowledge-forum-2022") &&
              isBefore(new Date(), new Date(2022, 11, 6))
            }
            extraMarketingPromptFeatureId="knowledge-forum-2022"
            extraMarketingPromptBody="It's not too late to register for our upcoming knowledge forum"
            extraMarketingPromptCta="Register now"
            extraMarketingPromptLink="https://www.eventbrite.co.uk/e/culture-shift-annual-knowledge-forum-2022-tickets-392983824197"
          >
            <div className="app-shell__body">
              <div className="app-shell__left-panel">
                <Navigation
                  isCollapsed={isNavigationCollapsed}
                  onToggleCollapse={handleOnToggleNavCollapse}
                  onClickLogOut={handleOnClickLogOut}
                />
              </div>
              <LockoutChecks>
                <Main>
                  {self.org.billingWarning === "BILL_OVERDUE" && !self.org.isSuspended ? <BillOverdue /> : null}
                  {self.org.billingWarning === "CONTRACT_EXPIRY_DUE" && !self.org.isSuspended ? (
                    <ContractExpiryDue />
                  ) : null}
                  <Suspense fallback={<LoadingPage />}>{children}</Suspense>
                </Main>
              </LockoutChecks>
            </div>
          </ToastProvider>

          {!self.isSsoUser && !self.hasMfaEnabled && !self.org.isSuspended ? <EnforceTwoFactorAuthModal /> : null}
        </div>
      </SelectProvider>
    </AlertProvider>
  );
}

export default AppShell;

const mainClassContext = createContext<(className: string) => void>(() => null);

function Main({ children }: PropsWithChildren<unknown>) {
  const [className, setClassName] = useState("");

  return (
    <mainClassContext.Provider value={setClassName}>
      <div className={`app-shell__content ${className}`}>
        <main className="main" id="main-content" tabIndex={-1}>
          {children}
        </main>
      </div>
    </mainClassContext.Provider>
  );
}

export function useMainClass(className: string) {
  const setClassName = useContext(mainClassContext);

  useEffect(() => {
    setClassName(className);
    return () => setClassName("");
  }, [className, setClassName]);
}
